import React from 'react';
import {
  Box,
  Chip,
  Typography,
  Tooltip,
  useTheme,
  alpha
} from '@mui/material';
import {
  Label as LabelIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getCategoryEmoji, isTrendingCategory, getCategoryColor } from '../../utils/categoryEmojiUtils';
import { useCategoryName } from '../../utils/categoryTranslation';

/**
 * Composant pour afficher les catégories d'un listing
 */
const CategoryDisplay = ({ 
  category,
  variant = 'chip', // 'chip', 'text', 'badge', 'detailed'
  size = 'small', // 'small', 'medium', 'large'
  showIcon = true,
  color = 'primary',
  maxWidth = null,
  onClick = null
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const getCategoryNameTranslated = useCategoryName();

  // Si pas de catégorie, ne rien afficher
  if (!category) {
    return null;
  }

  // Extraire les informations de la catégorie
  const categoryName = getCategoryNameTranslated(category) || category.name || category;
  const categoryId = category.id;
  const categorySlug = category.slug;
  
  // Déterminer si c'est une catégorie tendance
  const isTrending = isTrendingCategory(categoryName);
  const categoryColor = getCategoryColor(categoryName);

  // Mode chip (par défaut)
  if (variant === 'chip') {
    return (
      <Chip
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography component="span" sx={{ fontSize: size === 'small' ? '1em' : '1.2em', mr: 0.5 }}>
              {getCategoryEmoji(categoryName)}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {categoryName}
            </Typography>
          </Box>
        }
        onClick={onClick}
        variant="outlined"
        sx={{
          maxWidth: maxWidth,
          borderRadius: 2,
          cursor: onClick ? 'pointer' : 'default',
          transition: 'all 0.3s ease',
          bgcolor: isTrending 
            ? 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)'
            : 'transparent',
          border: isTrending ? `2px solid ${categoryColor}` : `1px solid ${alpha(categoryColor, 0.3)}`,
          color: isTrending ? '#2d1b1b' : 'inherit',
          '& .MuiChip-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          },
          '&:hover': onClick ? {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            bgcolor: isTrending ? 'rgba(255, 107, 53, 0.1)' : alpha(categoryColor, 0.1),
            borderColor: categoryColor
          } : {}
        }}
        title={categoryName.length > 20 ? categoryName : undefined}
      />
    );
  }

  // Mode badge
  if (variant === 'badge') {
    return (
      <Box
        onClick={onClick}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.5,
          borderRadius: 2,
          backgroundColor: isTrending 
            ? 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)'
            : alpha(categoryColor, 0.1),
          border: isTrending ? `2px solid ${categoryColor}` : `1px solid ${alpha(categoryColor, 0.3)}`,
          maxWidth: maxWidth,
          cursor: onClick ? 'pointer' : 'default',
          transition: 'all 0.3s ease',
          '&:hover': onClick ? {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            backgroundColor: isTrending ? 'rgba(255, 107, 53, 0.1)' : alpha(categoryColor, 0.15),
            borderColor: categoryColor
          } : {}
        }}
      >
        <Typography component="span" sx={{ fontSize: size === 'small' ? '0.9em' : '1em', mr: 0.5 }}>
          {getCategoryEmoji(categoryName)}
        </Typography>
        <Typography
          variant={size === 'small' ? 'caption' : 'body2'}
          sx={{
            fontWeight: 500,
            color: isTrending ? '#2d1b1b' : categoryColor,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={categoryName.length > 20 ? categoryName : undefined}
        >
          {categoryName}
        </Typography>
      </Box>
    );
  }

  // Mode text simple
  if (variant === 'text') {
    return (
      <Box
        onClick={onClick}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          maxWidth: maxWidth,
          cursor: onClick ? 'pointer' : 'default'
        }}
      >
        {showIcon && (
          <Typography component="span" sx={{ fontSize: size === 'small' ? '0.9em' : size === 'medium' ? '1em' : '1.1em' }}>
            {getCategoryEmoji(categoryName)}
          </Typography>
        )}
        <Typography
          variant={size === 'small' ? 'caption' : size === 'medium' ? 'body2' : 'body1'}
          color="text.secondary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            '&:hover': onClick ? {
              color: theme.palette[color]?.main || theme.palette.primary.main,
              textDecoration: 'underline'
            } : {}
          }}
          title={categoryName.length > 20 ? categoryName : undefined}
        >
          {categoryName}
        </Typography>
      </Box>
    );
  }

  // Mode detailed avec informations complètes
  if (variant === 'detailed') {
    return (
      <Box
        onClick={onClick}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          p: 1.5,
          borderRadius: 2,
          backgroundColor: isTrending 
            ? 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)'
            : alpha(categoryColor, 0.05),
          border: isTrending ? `2px solid ${categoryColor}` : `1px solid ${alpha(categoryColor, 0.2)}`,
          maxWidth: maxWidth,
          cursor: onClick ? 'pointer' : 'default',
          transition: 'all 0.3s ease',
          '&:hover': onClick ? {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
            backgroundColor: isTrending ? 'rgba(255, 107, 53, 0.1)' : alpha(categoryColor, 0.08),
            borderColor: categoryColor
          } : {}
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography component="span" sx={{ fontSize: '1.3em' }}>
            {getCategoryEmoji(categoryName)}
          </Typography>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            sx={{
              color: isTrending ? '#2d1b1b' : categoryColor,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1
            }}
          >
            {categoryName}
          </Typography>
          {isTrending && (
            <Typography variant="caption" sx={{ 
              px: 0.5, 
              py: 0.2, 
              bgcolor: 'rgba(255,255,255,0.3)', 
              borderRadius: 1,
              fontSize: '0.65rem',
              fontWeight: 'bold',
              color: '#2d1b1b'
            }}>
              🔥 TREND
            </Typography>
          )}
        </Box>
        
        {(categorySlug || categoryId) && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {categorySlug && (
              <Typography variant="caption" color="text.secondary">
                {t('category.slug', 'Slug')}: {categorySlug}
              </Typography>
            )}
            {categoryId && (
              <Typography variant="caption" color="text.secondary">
                ID: {categoryId}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  }

  // Fallback au mode chip
  return (
    <Chip
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography component="span" sx={{ fontSize: '1em', mr: 0.5 }}>
            {getCategoryEmoji(categoryName)}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {categoryName}
          </Typography>
        </Box>
      }
      onClick={onClick}
      variant="outlined"
      sx={{
        maxWidth: maxWidth,
        borderRadius: 2,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        bgcolor: isTrending 
          ? 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)'
          : 'transparent',
        border: isTrending ? `2px solid ${categoryColor}` : `1px solid ${alpha(categoryColor, 0.3)}`,
        color: isTrending ? '#2d1b1b' : 'inherit',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          bgcolor: isTrending ? 'rgba(255, 107, 53, 0.1)' : alpha(categoryColor, 0.1),
          borderColor: categoryColor
        } : {}
      }}
    />
  );
};

export default CategoryDisplay;
